/**
 * ViewAsRoleContext
 * Permite al admin simular la vista de otro rol en el sidebar.
 * Solo afecta la visibilidad del menu lateral, NO bloquea rutas.
 */
import React, { createContext, useContext, useState, useCallback, useMemo } from "react";
import { ROLES, ROLE_PERMISSIONS } from "../PermissionsContext/PermissionsContext";

const ROLE_LABELS = {
  [ROLES.ADMIN]: "Admin",
  [ROLES.PCP]: "PCP",
  [ROLES.CARE_MANAGEMENT]: "Care Management",
  [ROLES.CEO]: "CEO",
};

export const ViewAsRoleContext = createContext();

export const ViewAsRoleProvider = ({ children }) => {
  const [viewAsRole, setViewAsRole] = useState(null);

  const switchRole = useCallback((role) => {
    setViewAsRole(role || null);
  }, []);

  const resetRole = useCallback(() => {
    setViewAsRole(null);
  }, []);

  const viewAsPermissions = useMemo(() => {
    if (!viewAsRole) return null;
    return ROLE_PERMISSIONS[viewAsRole] || [];
  }, [viewAsRole]);

  /**
   * Verifica si un item/seccion es visible considerando el viewAsRole.
   * Si viewAsRole esta activo, usa los permisos de ese rol.
   * Si no, retorna null para que se use el sistema normal de permisos.
   */
  const hasViewAsPermission = useCallback(
    (permissions) => {
      if (!viewAsRole || !viewAsPermissions) return null; // No hay override
      if (!permissions || permissions.length === 0) return true;
      if (viewAsPermissions.includes("*")) return true;
      return permissions.some((p) => viewAsPermissions.includes(p));
    },
    [viewAsRole, viewAsPermissions]
  );

  const value = useMemo(
    () => ({
      viewAsRole,
      viewAsRoleLabel: viewAsRole ? ROLE_LABELS[viewAsRole] || viewAsRole : null,
      switchRole,
      resetRole,
      hasViewAsPermission,
      availableRoles: ROLE_LABELS,
    }),
    [viewAsRole, switchRole, resetRole, hasViewAsPermission]
  );

  return (
    <ViewAsRoleContext.Provider value={value}>
      {children}
    </ViewAsRoleContext.Provider>
  );
};

export const useViewAsRole = () => {
  const context = useContext(ViewAsRoleContext);
  if (!context) {
    throw new Error("useViewAsRole debe usarse dentro de ViewAsRoleProvider");
  }
  return context;
};

export default ViewAsRoleContext;
